import React, { useState, useEffect, useRef } from "react";

const FUNC_KEYS = "F1F2F3F4F5F6F7F8F9F10F11F12";

function getValue(
  value: string | number,
  variant: textBoxVariant,
  isInteger: boolean,
  decimalPoint: number
): string {
  switch (variant) {
    case "number":
      if (isInteger) {
        return Math.round(value as number).toFixed(0);
      } else {
        const numVal = Number(value);
        if (isNaN(numVal)) {
          return value.toString();
        }
        return (value as number).toFixed(decimalPoint);
      }
    case "text":
      return value as string;
    case "password":
      return value as string;
  }
}

type textBoxVariant = "text" | "number" | "password";

type Props = {
  name: string;
  value: string | number;
  onChange?: (value: string | number) => void;
  onClick?: () => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onDoubleClick?: (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => void;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
  className?: string;
  isInteger?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  variant?: textBoxVariant;
  placeHolder?: string;
  decimalPoint?: number;
  onErrorOccured?: (key: string) => void;
  onErrorCleared?: (key: string) => void;
  supportAutoFill?: boolean;
  minValueInclusive?: number;
  maxValueInclusive?: number;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
};

export default function LoginTextBox({
  name,
  value,
  onChange = () => {},
  onClick = () => {},
  onKeyPress = () => {},
  onKeyDown = () => {},
  onDoubleClick = () => {},
  onBlur = () => {},
  onInput = () => {},
  style = {},
  className = "",
  isInteger = false,
  disabled = false,
  readOnly = false,
  variant = "text",
  placeHolder,
  decimalPoint = 2,
  onErrorOccured = () => {},
  onErrorCleared = () => {},
  supportAutoFill = false,
  minValueInclusive = undefined,
  maxValueInclusive = undefined,
  onPaste = undefined,
}: Props) {
  const [isError, setIsError] = useState(false);
  const [tbValue, setTbValue] = useState(
    getValue(value, variant, isInteger, decimalPoint)
  );
  const textField = useRef<HTMLInputElement>(null);
  const inputType = variant === "number" ? "text" : variant;

  useEffect(() => {
    let interval = setInterval(() => {
      if (supportAutoFill && textField.current) {
        setTbValue(textField.current.value);
        onChange(textField.current.value);
        clearInterval(interval);
      }
    }, 100);
  });

  useEffect(() => {
    setTbValue(getValue(value, variant, isInteger, decimalPoint));
  }, [value, variant, isInteger, decimalPoint]);

  const onBlurElement = (e: React.FocusEvent<HTMLInputElement>): void => {
    let newValue = e.target.value;
    if (variant === "number") {
      let numberValue = parseFloat(newValue);
      if (isNaN(numberValue)) {
        setIsError(true);
        onErrorOccured(name);
        setTbValue(getValue(value, variant, isInteger, decimalPoint));
        return;
      }

      if (minValueInclusive != undefined && numberValue < minValueInclusive) {
        setIsError(true);
        onErrorOccured(name);
        setTbValue(getValue(value, variant, isInteger, decimalPoint));
        return;
      }

      if (maxValueInclusive != undefined && numberValue > maxValueInclusive) {
        setIsError(true);
        onErrorOccured(name);
        setTbValue(getValue(value, variant, isInteger, decimalPoint));
        return;
      }

      setIsError(false);
      onErrorCleared(name);
      if (isInteger) {
        numberValue = Math.round(numberValue);
      }

      setTbValue(getValue(numberValue, variant, isInteger, decimalPoint));

      if (numberValue !== value) {
        onChange(numberValue);
      }
    } else {
      setTbValue(newValue);
      onChange(newValue);
    }
    onBlur(e);
  };

  const onKeyPressInternal = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      textField.current?.blur();
    }
    onKeyPress(e);
  };

  const onKeyDownInternal = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (variant === "number") {
      const selectionStart = textField.current?.selectionStart ?? 0;
      const selectionEnd = textField.current?.selectionStart ?? 0;
      const oldText = textField.current?.value ?? "";
      const fullText =
        oldText.slice(0, selectionStart) +
        e.key +
        oldText.slice(selectionStart, selectionEnd);
      if (
        !FUNC_KEYS.includes(e.key) &&
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight" &&
        e.key !== "Backspace" &&
        e.key !== "Escape" &&
        e.key !== "Enter" &&
        e.key !== "Delete" &&
        e.key !== "Tab" &&
        e.key !== "Home" &&
        e.key !== "End" &&
        e.ctrlKey !== true &&
        !fullText.match(/^[-]?\d*\.?\d*$/)
      )
        e.preventDefault();
    }
    onKeyDown(e);
  };

  return (
    <input
      ref={textField}
      type={inputType}
      style={{
        minWidth: "40px",
        height: "34Px",
        paddingLeft: "5px",
        boxSizing: "border-box",
        background: disabled ? "#E0E0E0" : "white",
        // opacity: disabled ? 0.5 : 1,
        ...style,
      }}
      className={className}
      name={name}
      value={tbValue}
      onChange={readOnly ? () => {} : (e) => setTbValue(e.target.value)}
      onClick={onClick}
      onKeyPress={onKeyPressInternal}
      onKeyDown={onKeyDownInternal}
      onInput={onInput}
      onBlur={readOnly ? () => {} : onBlurElement}
      onDoubleClick={onDoubleClick}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeHolder}
      autoComplete={supportAutoFill ? "on" : "new-password"}
      onPaste={onPaste}
    />
  );
}
